import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import URLDisplay from '../Printer/URLDisplay';

const LiveVideo = () => {
    const [currentVideo, setCurrentVideo] = useState(() => sessionStorage.getItem('currentVideo') || '');
    const [playerReady, setPlayerReady] = useState(false);
    const [error, setError] = useState(null);
    const [isLive, setIsLive] = useState(false);
    const playerRef = useRef(null);

    useEffect(() => {
        const loadYouTubeAPI = () => {
            return new Promise((resolve, reject) => {
                if (window.YT) {
                    resolve(window.YT);
                    return;
                }
                const tag = document.createElement('script');
                tag.src = 'https://www.youtube.com/iframe_api';
                tag.onload = () => {
                    window.onYouTubeIframeAPIReady = () => {
                        resolve(window.YT);
                    };
                };
                tag.onerror = reject;
                document.body.appendChild(tag);
            });
        };

        loadYouTubeAPI()
            .then((YT) => {
                playerRef.current = new YT.Player('player', {
                    height: '500',
                    width: '100%',
                    playerVars: {
                        autoplay: 1,
                        modestbranding: 1,
                        controls: 1,
                        rel: 0,
                        iv_load_policy: 3,
                        origin: window.location.origin,
                    },
                    events: {
                        onReady: onPlayerReady,
                        onStateChange: onPlayerStateChange,
                        onError: onPlayerError,
                    },
                });
            })
            .catch((err) => {
                console.error('Failed to load YouTube API:', err);
                setError('Failed to load video player. Please try again later.');
            });

        return () => {
            if (playerRef.current) {
                playerRef.current.destroy();
            }
        };
    }, []);

    const onPlayerReady = () => {
        setPlayerReady(true);
        if (currentVideo) {
            loadVideo(currentVideo);
        }
    };

    const onPlayerStateChange = (event) => {
        if (event.data === window.YT.PlayerState.PLAYING) {
            const duration = event.target.getDuration();
            setIsLive(duration === 0);
        }
    };

    const loadVideo = (videoId) => {
        if (playerReady && playerRef.current && playerRef.current.loadVideoById) {
            playerRef.current.loadVideoById({
                videoId: videoId,
                suggestedQuality: 'large',
            });
        } else {
            console.error('Player is not ready or loadVideoById is not available');
        }
    };

    const fetchVideoUrl = async () => {
        try {
            const response = await axios.get('https://cmti-edge.online/foundry/php/urlHandler.php');
            const videoUrl = response.data.url;
            const videoId = extractVideoID(videoUrl);

            if (videoId) {
                setCurrentVideo(videoId);
                sessionStorage.setItem('currentVideo', videoId); // Save to sessionStorage
                loadVideo(videoId);
                setError(null);
                setIsLive(false);
            } else {
                setError('Invalid video URL received. Please try again.');
            }
        } catch (err) {
            setError('Failed to fetch video URL. Please try again later.');
        }
    };

    // Helper function to extract the video ID from full YouTube URLs
    const extractVideoID = (url) => {
        const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=|live\/)|youtu\.be\/)([^&\n?#]+)/;
        const match = url.match(regex);
        return match && match[1] ? match[1] : null;
    };

    const onPlayerError = (event) => {
        const errorCode = event.data;
        let errorMessage = '';
        switch (errorCode) {
            case 2:
                errorMessage = 'The video ID is invalid. Please check and try again.';
                break;
            case 5:
                errorMessage = 'There was an error with the HTML5 player. Please try again.';
                break;
            case 100:
                errorMessage = 'The requested video was not found. Please try another code.';
                break;
            case 101:
            case 150:
                errorMessage =
                    'This video is restricted and cannot be played in this player. Please ensure the video is public and embeddable.';
                break;
            default:
                errorMessage = 'An unknown error occurred while trying to play the video.';
                break;
        }
        setError(errorMessage);
        console.error(`YouTube Player Error (${errorCode}):`, errorMessage);
    };

    return (
        <div className="live-video-container flex flex-col justify-center items-center p-6">
            <h1 className="text-2xl font-bold text-center mb-6 text-white">Watch Live 3D Printing Video</h1>
            <div>
                <URLDisplay />
            </div>
            <button
                onClick={fetchVideoUrl}
                className="bg-gradient-to-r w-64 from-blue-400 to-blue-600 text-white px-3 py-3 rounded-lg hover:from-blue-500 hover:to-blue-700 focus:outline-none transition duration-300 ease-in-out mb-4"
            >
                Load Video
            </button>



            {error && <div className="error-message text-red-500 mb-4">{error}</div>}

            {isLive && <div className="live-indicator text-green-500 mb-4">LIVE</div>}

            <div className="video-frame max-w-4xl w-full">
                <div id="player"></div>
            </div>
        </div>
    );
};

export default LiveVideo;
